/**
 * One movement in the library — the whole row is the control.
 *
 * It was `features/exercise-library-rail/ui/RailRow` until 17 September 2026 and moved down here
 * with `ExerciseLibraryList`, when the survey editor's recording picker became the same library in a
 * dialog. Two things changed on the way:
 *
 * · **The DRAG left.** `entities/` may not import `entities/`, and the drag payload is a plan
 *   draft's `ExercisePick` — so the gesture stays where the plan lives. The row still renders the
 *   `<li>` the gesture needs: the builder hands in `rowRef` and `isDragging` from its own
 *   `useDraggableItem`, and the dashed source treatment is still this stylesheet's. A host that
 *   drags nothing passes neither and nothing is bound.
 * · **The muscle hue reads through `shared/ui/muscleColour`.** It used to ask
 *   `entities/plan-draft`'s `displayGroupOf` for the fourteen-to-six rollup; that table moved to
 *   `shared/` so both entities could read the one copy.
 *
 * ── Three paths to a press, and only the pointer one picks a POSITION ────────────────────────
 * Pressing the row does whatever a press means on the surface it is on — choose it, insert it, name
 * it; so does `E` while it is focused. Dragging it, where a host binds one, inserts at the position
 * the drop indicator shows. The keyboard path deliberately does not pick a position: inserting at
 * the end and then moving the new block with its own grip reaches every position a drag can, through
 * two acts a screen-reader user already knows, and it avoids inventing a second position picker with
 * no canon behind it.
 */

import { useState, type RefObject } from 'react';

import { BarbellGlyph } from '~/shared/ui/ExerciseGlyphs';
import { muscleColour, muscleColourGroupOf } from '~/shared/ui/muscleColour';

import { stillUrl, type ExerciseListItem } from '../model/exerciseListItem';

import styles from './ExerciseLibraryRow.module.css';

/** Which drawing the row takes. See `ExerciseLibraryList`'s own `variant` for what each host is. */
export type ExerciseLibraryVariant = 'drawer' | 'dialog';

export interface ExerciseLibraryRowProps {
  readonly item: ExerciseListItem;
  /** The control's description — the movement's own name is its NAME. */
  readonly actionLabel: string;
  /** The type pill's word, resolved by the host. `null` when the backend named no type. */
  readonly typeLabel: string | null;
  /** The primary group's word — the upstream `exerciseLibrary.muscles.*` copy. */
  readonly muscleLabel: string | null;
  /** "Sztanga · ławka", assembled by the host so the separator stays translatable. */
  readonly equipmentLabel: string | null;
  /**
   * Which width this row is drawn at, and therefore which shape it takes.
   *
   * `compact` is the calendar's 240px column: a 34×22 plate, the name, and "typ · sprzęt" under it.
   * `drawer` is the programator mock's 320px row: a 48×36 plate, the name on ONE line, and a single
   * chip under it — the primary muscle, tinted in its own hue.
   * `dialog` is the survey editor's recording picker: the drawer's plate and card, and the compact
   * column's "typ · sprzęt" line. **No muscle caption** — owner, 17 September 2026. A trainer
   * picking the one movement a question asks about is not reading the list by body part, and the
   * `Mięśnie` tab above is where they go when they are.
   *
   * One prop rather than a boolean per feature: these are three drawings of one row, and an
   * `isCompact` / `showsChips` pair would let a caller ask for a shape none of them is.
   */
  readonly variant: ExerciseLibraryVariant;
  /**
   * Whether this row is CHOSEN, or `null` where a press acts straight away.
   *
   * One prop rather than a `selectable` / `isSelected` pair: the two would let a caller ask for a
   * row that can be chosen and is nothing, which is not a state.
   */
  readonly selected: boolean | null;
  /** The press. What it MEANS is the host's — choose, insert, or name. */
  readonly onPress: () => void;
  /**
   * The pointer entered or left this row — what the drawer's hover peek is driven by.
   *
   * Optional, and passed by the drawer alone: the two columns beside a day have nowhere to put a
   * 236px card and are the surfaces a drag comes out of, and the dialog has no peek. Reported for a
   * MOUSE only, so a tap on a touch screen does not flash a card a finger can neither read past nor
   * dismiss.
   */
  readonly onHoverChange?: (isHovering: boolean) => void;
  /**
   * The `<li>` a host's own drag gesture binds to, and whether that gesture is in flight.
   *
   * Both supplied by `features/exercise-library-rail` and by nothing else. The element MOVES, so the
   * ref belongs on the row rather than on anything inside it; `isDragging` draws the source's dashed,
   * emptied treatment — dimmed rather than removed, because pulling the row out of a scrolling list
   * would drag every result below it up under the pointer.
   */
  readonly rowRef?: RefObject<HTMLLIElement | null>;
  readonly isDragging?: boolean;
}

export function ExerciseLibraryRow({
  item,
  actionLabel,
  typeLabel,
  muscleLabel,
  equipmentLabel,
  variant,
  selected,
  onPress,
  onHoverChange,
  rowRef,
  isDragging = false,
}: ExerciseLibraryRowProps) {
  // The drawer alone draws the muscle caption; both remaining variants share the mock's plate and
  // card, which is what `.itemWide` carries — the 240px `compact` row it told apart is gone.
  const isDrawer = variant === 'drawer';
  const group = muscleColourGroupOf(item.primaryMuscles[0]);

  // The compact column and the dialog say "typ · sprzęt" on one line. The drawer says neither — it
  // draws ONE chip, the muscle, and nothing else (see the type-chip note below).
  const detail = [typeLabel, equipmentLabel].filter((part) => part !== null).join(' · ');

  // The hue this movement is read by: its primary group's. `null` on a movement the catalogue could
  // not attribute — the plate then keeps the neutral ground rather than inventing a group.
  const tint = group === null ? null : muscleColour(group);

  // A still the catalogue NAMES but the media root does not hold. Ten of the 467 named stills 404
  // against the local root, and a 404 with `alt=""` is not nothing on screen: Chrome draws its
  // broken-file glyph on a pale plate, which reads worse among tiles than the empty box did among
  // photographs. So a failed fetch falls back to the same tile — the row has no photograph either
  // way. A row is keyed by the movement's id, so a different movement is a different element and
  // the flag never carries over to one whose still is fine.
  const [coverFailed, setCoverFailed] = useState(false);
  const still = stillUrl(item);

  return (
    <li
      ref={rowRef}
      className={`${String(styles.item)} ${String(styles.itemWide)} ${
        isDrawer ? String(styles.itemDrawer) : ''
      }`}
      data-dragging={isDragging ? '' : undefined}
      onPointerEnter={(event) => {
        if (event.pointerType === 'mouse') onHoverChange?.(true);
      }}
      onPointerLeave={(event) => {
        if (event.pointerType === 'mouse') onHoverChange?.(false);
      }}
    >
      <button
        type="button"
        className={styles.row}
        title={actionLabel}
        /* The row is ONE control, so the chosen state is carried by the button that is already
         * there rather than by a checkbox beside it: a second control would take a press away from
         * the row, would double the tab stops in a 477-row list, and would need a name of its own
         * saying the same movement twice. Space and Enter both reach it because it is a button. */
        aria-pressed={selected ?? undefined}
        onClick={onPress}
        onKeyDown={(event) => {
          // The library's printed shortcut. Guarded on the modifiers so it cannot fire from a
          // browser or OS chord that happens to contain the letter. It means whatever a press means
          // on this surface — choose the row, insert the movement — because a row has one act.
          if (event.key.toLowerCase() === 'e' && !event.metaKey && !event.ctrlKey && !event.altKey) {
            event.preventDefault();
            onPress();
          }
        }}
      >
        {/* The mock's 18px box at radius 5: empty on `--border-2`, and filled with `--text` under a
          * `--bg-card` tick once the row is chosen. Drawn only where a press CHOOSES — the surfaces
          * where it acts straight away have nothing to mark. */}
        {selected !== null && (
          <span className={styles.check} aria-hidden="true">
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="m4.5 10.5 3.6 3.6L15.5 6.5" />
            </svg>
          </span>
        )}
        {still === null || coverFailed ? (
          // The tile a movement with NO photograph draws: the plate filled with its own muscle hue
          // at 13%, carrying the barbell in that hue — the mock's `phSt`, and the same fallback the
          // week card's rows already draw, from the same drawing.
          //
          // It was reached by 395 of the 477 rows until 9 September 2026, and that was a defect
          // rather than the catalogue being thin. This branch read `coverImageUrl`, which 82
          // movements carry; the wire also carries `posterUrl`, which 385 carry, and only 66 name
          // both. `stillUrl` takes them in that order and 397 of the 477 now draw a real
          // photograph — against 76 before. The rule never changed: a movement with no photograph
          // is drawn, not left as a hole. What changed is which movements actually have one.
          <span
            className={`${String(styles.photo)} ${String(styles.photoFallback)}`}
            style={
              tint === null
                ? undefined
                : { background: `color-mix(in srgb, ${tint} 13%, transparent)`, color: tint }
            }
            aria-hidden="true"
          >
            <BarbellGlyph className={styles.photoGlyph} strokeWidth={1.9} />
          </span>
        ) : (
          <img
            className={styles.photo}
            src={still}
            alt=""
            loading="lazy"
            onError={() => {
              setCoverFailed(true);
            }}
          />
        )}
        <span className={styles.text}>
          <span className={styles.name}>{item.name}</span>
          <span className={styles.meta}>
            {/* The drawer's one muscle tag: a dot in the group's hue and the group's word in its
              * ink, with no pill behind them (owner, 15 September 2026). The DOT carries the colour
              * and the word carries the meaning, so nothing here is colour-only — and the ink
              * follows the mock's own per-theme rule through `--chip-tint-ink-hue`.
              *
              * The mock draws a second chip beside it, `kindChip`, naming the movement's TYPE.
              * OWNER, 9 September 2026: "wyrzuciłbym SIŁOWE, MASA CIAŁA etc z listy, za duży noise
              * to jest". So the type leaves the row and stays where a trainer goes to choose BY it:
              * the panel's `Typ` tab, which already filters on the same facet. */}
            {isDrawer && tint !== null && muscleLabel !== null && (
              <span
                className={styles.muscleChip}
                style={{
                  color: `color-mix(in srgb, ${tint} var(--chip-tint-ink-hue), var(--text-2))`,
                }}
              >
                <i className={styles.dot} style={{ background: tint }} aria-hidden="true" />
                {muscleLabel}
              </span>
            )}
            {!isDrawer && detail.length > 0 && <span className={styles.detail}>{detail}</span>}
          </span>
        </span>
      </button>
    </li>
  );
}
